import { StyleSheet, Text, View } from 'react-native'
import { DSColor, Palette, Colors, getInitials } from '@ppwr/poker'
import { fontFamily } from '../../../theme'
import { PlayerCellVideo } from '../PlayerCell/PlayerCellVideo'
import { useResponsive } from '@/screens/new-design/hooks'

const getRandomColor = () => Colors[Math.floor(Math.random() * Colors.length - 1)]

// styles for video/initials according to number of players
const AVATAR_SIZES = {
  3: {
    width: 52,
    height: 52,
    borderRadius: 26,
    fontSize: 18
  },
  4: {
    width: 32,
    height: 32,
    borderRadius: 16,
    fontSize: 12
  },
  profile: {
    width: 48,
    height: 48,
    borderRadius: 36,
    fontSize: 12
  },
  default: {
    height: 72,
    width: 72,
    borderRadius: 36,
    fontSize: 20
  }
}

type AvatarSize = keyof typeof AVATAR_SIZES
type AvatarSizeConfig = (typeof AVATAR_SIZES)[AvatarSize]

interface VideoConfig {
  playerId: number
  right?: boolean
}

interface PlayerAvatarProps {
  name: string
  size: AvatarSize
  color?: DSColor
  video?: VideoConfig
  invert?: boolean
  small?: boolean
  applyOpacity?: boolean
}

export const PlayerAvatar = ({ name, color, video, size, invert, applyOpacity }: PlayerAvatarProps) => {
  const { verticalScale } = useResponsive()
  const avatarSize = AVATAR_SIZES[size] ?? AVATAR_SIZES.default

  if (video)
    return (
      <PlayerAvatarVideo
        video={video}
        size={{
          ...avatarSize,
          height: verticalScale(avatarSize.height),
          width: verticalScale(avatarSize.width),
          fontSize: verticalScale(avatarSize.fontSize)
        }}
      />
    )

  const colorToUse = color ?? getRandomColor()

  const primary = Colors.includes(colorToUse) ? Palette[colorToUse] : color ? Palette[color] : Palette['Brand/Cream']

  const getTextColor = () => {
    if (primary === undefined || primary === Palette['Global/White']) return Palette['Global/Black']
    return Palette['Global/White']
  }

  return (
    <View
      style={[
        styles.container,
        invert
          ? {
              backgroundColor: primary,
              borderColor: primary !== Palette['Brand/Cream'] ? primary : Palette['Global/Black']
            }
          : {},
        {
          ...avatarSize,
          height: verticalScale(avatarSize.height),
          width: verticalScale(avatarSize.width)
        },
        { opacity: applyOpacity ? 0.4 : 1 }
      ]}
    >
      <Text style={[[styles.text, { fontSize: verticalScale(12) }], { color: getTextColor() }]}>
        {getInitials(name)}
      </Text>
    </View>
  )
}

export const PlayerAvatarVideo = ({ video, size }: { video: VideoConfig; size: AvatarSizeConfig }) => {
  const { playerId, right } = video
  return (
    <View style={[styles.video, right ? styles.right : {}, size]}>
      <PlayerCellVideo
        playerId={playerId}
        videoStyle={{ borderRadius: size.borderRadius, overflow: 'hidden', flex: 1 }}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: Palette['Brand/Cream'],
    borderRadius: 40,
    borderWidth: 1,
    height: 40,
    width: 40,
    alignItems: 'center',
    justifyContent: 'center'
  },
  video: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: Palette['Global/Black']
  },
  right: {
    textAlign: 'right',
    alignSelf: 'flex-end'
  },
  text: {
    textAlign: 'center',
    fontSize: 12,
    fontFamily: fontFamily.GTAmericaExtended,
    textTransform: 'uppercase'
  },
  invert: {
    color: 'white'
  },
  notinvert: {
    color: '#000'
  }
})
